import React, { useEffect, useRef, useState } from "react"

import { motion } from "framer-motion"
import { NotificationManager } from "react-notifications"
import { gql } from "urql"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import EventEmitter from "src/EventEmitter"
import ordered from "src/ordered"
import { adminClient } from "src/urql-client"

import { useMutation, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import AutocompleteSelect from "components/Forms/AutocompleteSelect"
import Pagination from "components/Pagination"
import RemoveButton from "components/RemoveButton"

import Search from "./Search"

const MatchedUsers = ({
  sourceUserId,
  sourceKey,
  autocompletePath,
  searchAutocompletePath,
  loadUsersQuery,
  matchUserMutation,
  unmatchUserMutation,
}) => {
  const selectRef = useRef(null)
  const [page, setPage] = useState(1)
  const [generating, setGenerating] = useState(false)

  const [result, refetch] = useQuery({
    query: loadUsersQuery,
    variables: { sourceUserId, page },
    requestPolicy: "cache-and-network",
  })

  const [, runMatchUser] = useMutation(matchUserMutation)
  const [, runUnmatchUser] = useMutation(unmatchUserMutation)

  const onChange = value => {
    if (!value?.value) return
    runMatchUser({ sourceUserId, matchUserId: value.value }).then(() => {
      selectRef.current.clearValue()
    })
  }

  const removeUser = user => {
    runUnmatchUser({ sourceUserId, matchUserId: user.id })
  }

  const [, runMutation] = useMutation(requestNumberMutation, adminClient)

  const requestProxyNumbers = matchedUserId => {
    const tutorId = sourceKey === "tutor" ? sourceUserId : matchedUserId
    const studentId = sourceKey === "student" ? sourceUserId : matchedUserId
    setGenerating(true)

    runMutation({ tutorId, studentId })
      .then(
        response => {
          const { failures } = response.data.requestProxyNumber
          if (failures.length > 0) {
            NotificationManager.error("Something went wrong")
            setGenerating(false)
          } else NotificationManager.info("Generation in progress")
        },
        err => setErrorMessage("Something went wrong")
      )
      .catch(err => setErrorMessage("Something went wrong"))
  }

  useWebsocket({ channelName: "UserChannel", channelProps: {} })

  useEffect(() => {
    const handleNumberUpdate = () => {
      refetch()
      NotificationManager.success("Masked number generation complete")
      setGenerating(false)
    }

    EventEmitter.subscribe("numberCreated", handleNumberUpdate)
    return () => {
      EventEmitter.unsubscribe("numberCreated", handleNumberUpdate)
    }
  }, [refetch])

  return (
    <WithLoadingIndicator
      result={result}
      error="An error occurred while loading matched users"
      header={
        <div className="mb-4 flex gap-x-4">
          <h4 className="m-0">Matched Users</h4>
          <div className="grow">
            <Search autocompletePath={searchAutocompletePath} />
          </div>
        </div>
      }
    >
      {({ data }) => (
        <>
          <div className="row">
            <div className="col-12">
              <div className="mb-3">
                <AutocompleteSelect
                  ref={selectRef}
                  api={autocompletePath}
                  onChange={onChange}
                  placeholder="Match New User..."
                  searchOptions="published=true"
                />
              </div>
            </div>
          </div>

          <div className="mx-3">
            <div className="row">
              {ordered(data[sourceKey].matchedUsers.data, "lastName").map(
                user => {
                  return (
                    <motion.div
                      key={user.id}
                      positionTransition
                      className="col-12 col-md-6 col-xl-4 mb-4 flex items-center rounded-md p-2 odd:bg-gray-200"
                    >
                      <div>
                        <RemoveButton onClick={() => removeUser(user)} />
                      </div>
                      <div>
                        <a href={user.showPath}>
                          <span>{user.fullName}</span>
                        </a>
                        {!(
                          user.studentToTutorPhoneNumber &&
                          user.parentToTutorPhoneNumber &&
                          user.secondaryParentToTutorPhoneNumber
                        ) && (
                          <div>
                            <button
                              className="btn btn-solid btn-info btn-sm"
                              onClick={() => requestProxyNumbers(user.id)}
                              disabled={generating}
                            >
                              Generate Masked Numbers
                            </button>
                          </div>
                        )}

                        <div className="font-weight-bold mt-2">
                          Tutor &lt;&gt; Student
                        </div>
                        <div>
                          Student dials: {user.studentToTutorPhoneNumber}
                        </div>
                        <div>Tutor dials: {user.tutorToStudentPhoneNumber}</div>

                        <div className="font-weight-bold mt-2">
                          Tutor &lt;&gt; Parent
                        </div>
                        <div>Parent dials: {user.parentToTutorPhoneNumber}</div>
                        <div>Tutor dials: {user.tutorToParentPhoneNumber}</div>

                        <div className="font-weight-bold mt-2">
                          Tutor &lt;&gt; Secondary Parent
                        </div>
                        <div>
                          Parent dials: {user.secondaryParentToTutorPhoneNumber}
                        </div>
                        <div>
                          Tutor dials: {user.tutorToSecondaryParentPhoneNumber}
                        </div>
                      </div>
                    </motion.div>
                  )
                }
              )}
            </div>
            <div className="row">
              <Pagination
                totalPages={data[sourceKey].matchedUsers.totalPages}
                currentPage={data[sourceKey].matchedUsers.currentPage}
                changePage={setPage}
              />
            </div>
          </div>
        </>
      )}
    </WithLoadingIndicator>
  )
}

const requestNumberMutation = gql`
  mutation ($studentId: ID!, $tutorId: ID!) {
    requestProxyNumber(studentId: $studentId, tutorId: $tutorId) {
      failures {
        message
      }
    }
  }
`

export default MatchedUsers
